import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["exerciseList", "template", "emptyState", "setCount", "libraryList", "libraryTab", "librarySearch", "libraryEmpty", "modalWrapper", "addSelectedBtn", "reorderModal", "reorderList"]

  connect() {
    const cards = this.exerciseListTarget.querySelectorAll('.workout-exercise-card:not([style*="display: none"])')
    this.exerciseCount = cards.length
    this.selectedExercises = new Set()
    this.activeLibrary = "library"
    this.updateUI()
    this.updateLibrary()
    
    // Only initialize sorting on desktop. On mobile, we use the Rearrange modal
    // to keep the main form scrollable without accidentally dragging.
    if (!this.isMobile()) {
      this.initializeSortable()
    }
    this.initializeReorderSortable()
  }

  disconnect() {
    this.toggleScrollLock(false, true)
  }

  initializeSortable() {
    if (!window.Sortable) {
      setTimeout(() => this.initializeSortable(), 100);
      return;
    }

    this.sortable = new Sortable(this.exerciseListTarget, {
      animation: 150,
      handle: '.workout-exercise-card',
      ghostClass: 'bg-blue-50',
      onEnd: () => {
        this.reorderExercises()
      }
    })
  }

  initializeReorderSortable() {
    if (!window.Sortable) {
      setTimeout(() => this.initializeReorderSortable(), 100);
      return;
    }

    if (this.hasReorderListTarget) {
      this.reorderSortable = new Sortable(this.reorderListTarget, {
        animation: 150,
        ghostClass: 'bg-blue-600/20',
        onEnd: () => {
          this.updateReorderNumbers()
        }
      })
    }
  }

  openReorderModal(event) {
    if (event) event.preventDefault()
    this.renderReorderList()
    this.reorderModalTarget.classList.remove("hidden")
    this.toggleScrollLock(true)
  }

  closeReorderModal(event) {
    if (event) event.preventDefault()
    this.reorderModalTarget.classList.add("hidden")
    this.toggleScrollLock(false)
  }

  renderReorderList() {
    const visibleCards = Array.from(this.exerciseListTarget.querySelectorAll('.workout-exercise-card:not([style*="display: none"])'))
    
    this.reorderListTarget.innerHTML = visibleCards.map((card, index) => {
      const title = card.querySelector('h4').textContent
      const exerciseId = card.dataset.index // Use data-index to track original element
      
      return `
        <div class="reorder-item bg-white/5 border border-white/10 rounded-2xl p-4 flex items-center justify-between gap-4" data-original-index="${exerciseId}">
          <div class="flex items-center gap-3 overflow-hidden">
            <span class="w-6 h-6 rounded-full bg-blue-600/20 text-blue-400 flex items-center justify-center text-[10px] font-black shrink-0">${index + 1}</span>
            <p class="text-sm font-black text-white truncate uppercase tracking-tight">${title}</p>
          </div>
          <div class="flex items-center gap-1 shrink-0">
            <button type="button" data-action="click->workout-builder#moveExerciseUp" class="p-2 text-white/40 hover:text-white hover:bg-white/10 rounded-xl transition">
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="3" d="M5 15l7-7 7 7"></path></svg>
            </button>
            <button type="button" data-action="click->workout-builder#moveExerciseDown" class="p-2 text-white/40 hover:text-white hover:bg-white/10 rounded-xl transition">
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="3" d="M19 9l-7 7-7-7"></path></svg>
            </button>
          </div>
        </div>
      `
    }).join('')
  }

  moveExerciseUp(event) {
    const item = event.currentTarget.closest('.reorder-item')
    const prev = item.previousElementSibling
    if (prev) {
      item.parentNode.insertBefore(item, prev)
      this.updateReorderNumbers()
    }
  }

  moveExerciseDown(event) {
    const item = event.currentTarget.closest('.reorder-item')
    const next = item.nextElementSibling
    if (next) {
      item.parentNode.insertBefore(next, item)
      this.updateReorderNumbers()
    }
  }

  updateReorderNumbers() {
    this.reorderListTarget.querySelectorAll('.reorder-item').forEach((item, index) => {
      item.querySelector('span').textContent = index + 1
    })
  }

  applyReorder(event) {
    if (event) event.preventDefault()
    
    const reorderedItems = Array.from(this.reorderListTarget.querySelectorAll('.reorder-item'))
    const originalCards = Array.from(this.exerciseListTarget.querySelectorAll('.workout-exercise-card:not([style*="display: none"])'))
    
    // Clear and re-append in new order
    reorderedItems.forEach(item => {
      const originalIndex = item.dataset.originalIndex
      const card = originalCards.find(c => c.dataset.index === originalIndex)
      if (card) {
        this.exerciseListTarget.appendChild(card)
      }
    })
    
    this.reorderExercises()
    this.closeReorderModal()
  }

  openLibrary(event) {
    if (event) event.preventDefault()
    this.modalWrapperTarget.classList.remove("hidden")
    this.toggleScrollLock(true)
    this.selectedExercises.clear()
    this.updateSelectionUI()
  }

  closeLibrary(event) {
    if (event) event.preventDefault()
    this.modalWrapperTarget.classList.add("hidden")
    this.toggleScrollLock(false)
  }

  toggleScrollLock(lock, force = false) {
    const body = document.body
    const html = document.documentElement
    let openModals = parseInt(body.dataset.openModals || "0")

    if (lock) {
      openModals++
      body.dataset.openModals = openModals
      
      if (openModals === 1) {
        this.scrollPos = window.pageYOffset
        body.style.position = 'fixed'
        body.style.top = `-${this.scrollPos}px`
        body.style.width = '100%'
        body.classList.add('overflow-hidden')
        html.classList.add('overflow-hidden')
      }
    } else {
      openModals = force ? 0 : Math.max(0, openModals - 1)
      body.dataset.openModals = openModals
      
      if (openModals === 0) {
        const scrollY = body.style.top
        body.style.position = ''
        body.style.top = ''
        body.style.width = ''
        body.classList.remove('overflow-hidden')
        html.classList.remove('overflow-hidden')
        if (!force) {
          window.scrollTo(0, parseInt(scrollY || '0') * -1)
        }
      }
    }
  }

  isMobile() {
    return window.innerWidth < 1024
  }

  handleExerciseClick(event) {
    if (this.isMobile()) {
      this.toggleSelection(event)
    } else {
      this.addExercise(event)
    }
  }

  toggleSelection(event) {
    const item = event.currentTarget
    const id = item.dataset.exerciseId
    const title = item.dataset.exerciseTitle

    if (this.selectedExercises.has(id)) {
      this.selectedExercises.delete(id)
      item.classList.remove('bg-blue-600', 'ring-4', 'ring-blue-400')
      item.classList.add('bg-white/5')
    } else {
      this.selectedExercises.add(id)
      item.classList.remove('bg-white/5')
      item.classList.add('bg-blue-600', 'ring-4', 'ring-blue-400')
    }
    
    this.updateSelectionUI()
  }

  updateSelectionUI() {
    const count = this.selectedExercises.size
    const btn = this.element.querySelector('[data-workout-builder-target="addSelectedBtn"]')
    if (btn) {
      btn.classList.toggle('hidden', count === 0)
      btn.textContent = `Add ${count} ${count === 1 ? 'Exercise' : 'Exercises'} to Workout`
    }
  }

  addSelectedExercises(event) {
    if (event) event.preventDefault()
    
    const items = Array.from(this.libraryListTarget.querySelectorAll('.exercise-item'))
    const selectedItems = items.filter(item => this.selectedExercises.has(item.dataset.exerciseId))

    selectedItems.forEach(item => {
      this.insertExercise(item.dataset.exerciseId, item.dataset.exerciseTitle)
    })

    this.selectedExercises.clear()
    this.closeLibrary()
    this.updateUI()
  }

  addExercise(event) {
    if (event) event.preventDefault()
    const id = event.currentTarget.dataset.exerciseId
    const title = event.currentTarget.dataset.exerciseTitle
    
    this.insertExercise(id, title)
    
    // Visual feedback
    event.currentTarget.classList.add('bg-green-600')
    setTimeout(() => {
      event.currentTarget.classList.remove('bg-green-600')
    }, 200)
    
    this.updateUI()
  }

  insertExercise(id, title) {
    const timestamp = new Date().getTime() + Math.floor(Math.random() * 10000)
    let content = this.templateTarget.innerHTML
      .replace(/INDEX/g, timestamp)
      .replace(/EXERCISE_ID/g, id)
      .replace(/EXERCISE_TITLE/g, title)
      .replace(/POS/g, this.exerciseCount + 1)
      .replace(/POS_VAL/g, this.exerciseCount)

    this.exerciseListTarget.insertAdjacentHTML('beforeend', content)
    this.reorderExercises()
  }

  removeExercise(event) {
    const card = event.currentTarget.closest('.workout-exercise-card')
    const destroyInput = card.querySelector('.destroy-input')
    
    if (destroyInput) {
      // Existing record, mark for destruction and hide
      destroyInput.value = '1'
      card.style.display = 'none'
    } else {
      // New record, just remove from DOM
      card.remove()
    }
    
    this.reorderExercises()
    this.updateUI()
  }

  reorderExercises() {
    const visibleCards = this.exerciseListTarget.querySelectorAll('.workout-exercise-card:not([style*="display: none"])')
    visibleCards.forEach((card, index) => {
      const posSpan = card.querySelector('span')
      if (posSpan) posSpan.textContent = index + 1
      
      const posInput = card.querySelector('.position-input')
      if (posInput) posInput.value = index
    })
    this.exerciseCount = visibleCards.length
  }

  filterExercises(event) {
    this.updateLibrary(event.target.value)
  }

  switchLibrary(event) {
    event.preventDefault()
    this.activeLibrary = event.currentTarget.dataset.library
    this.updateLibrary()
  }

  updateLibrary(query = this.hasLibrarySearchTarget ? this.librarySearchTarget.value : "") {
    if (!this.hasLibraryListTarget) return

    const normalizedQuery = query.toLowerCase()
    const items = this.libraryListTarget.querySelectorAll('.exercise-item')

    items.forEach(item => {
      const name = item.dataset.exerciseName
      const matchesLibrary = item.dataset.exerciseLibrary === this.activeLibrary
      item.classList.toggle('hidden', !matchesLibrary || !name.includes(normalizedQuery))
    })

    this.libraryTabTargets.forEach(tab => {
      const active = tab.dataset.library === this.activeLibrary
      tab.classList.toggle("bg-white", active)
      tab.classList.toggle("text-gray-900", active)
      tab.classList.toggle("shadow-md", active)
      tab.classList.toggle("text-white/50", !active)
      tab.setAttribute("aria-selected", active.toString())
    })

    this.libraryEmptyTargets.forEach(emptyState => {
      const forActiveLibrary = emptyState.dataset.library === this.activeLibrary
      const hasVisibleItems = Array.from(items).some(item => !item.classList.contains("hidden"))
      emptyState.classList.toggle("hidden", !forActiveLibrary || hasVisibleItems)
    })
  }

  updateUI() {
    const visibleCards = this.exerciseListTarget.querySelectorAll('.workout-exercise-card:not([style*="display: none"])')
    if (visibleCards.length > 0) {
      this.emptyStateTarget.classList.add('hidden')
    } else {
      this.emptyStateTarget.classList.remove('hidden')
    }
    
    if (this.hasSetCountTarget) {
      this.setCountTarget.textContent = `${visibleCards.length} ${visibleCards.length === 1 ? 'exercise' : 'exercises'}`
    }
  }
}
